import { useState, useEffect, useCallback } from "react";
import { RegistryItem } from "../lib/supabase";
import {
  registryAPI,
  CreateRegistryItemData,
  UpdateRegistryItemData,
} from "../api/registry";

export const useRegistry = () => {
  const [items, setItems] = useState<RegistryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await registryAPI.getAllItems();
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, []);

  const createItem = useCallback(
    async (itemData: CreateRegistryItemData) => {
      try {
        setError(null);
        await registryAPI.createItem(itemData);
        await fetchItems(); // Refresh the list
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to create item");
        throw err;
      }
    },
    [fetchItems]
  );

  const updateItem = useCallback(
    async (itemId: string, updateData: UpdateRegistryItemData) => {
      try {
        setError(null);
        await registryAPI.updateItem(itemId, updateData);
        await fetchItems(); // Refresh the list
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to update item");
        throw err;
      }
    },
    [fetchItems]
  );

  const markAsPurchased = useCallback(
    async (
      itemId: string,
      purchasedByName: string,
      purchasedById?: string,
      note?: string
    ) => {
      try {
        setError(null);
        await registryAPI.markAsPurchased(
          itemId,
          purchasedByName,
          purchasedById,
          note
        );
        await fetchItems(); // Refresh the list
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to mark item as purchased"
        );
        throw err;
      }
    },
    [fetchItems]
  );

  const deleteItem = useCallback(
    async (itemId: string) => {
      try {
        setError(null);
        await registryAPI.deleteItem(itemId);
        await fetchItems(); // Refresh the list
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to delete item");
        throw err;
      }
    },
    [fetchItems]
  );

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return {
    items,
    loading,
    error,
    fetchItems,
    createItem,
    updateItem,
    markAsPurchased,
    deleteItem,
  };
};
